import { Icon, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";
import type { HubChromeScriptGroup } from "./hub-chrome.types.js";
import { OpportunityHubScriptCountBadge } from "./OpportunityHubScriptCountBadge.js";
import { OpportunityHubScriptSentBadge } from "./OpportunityHubScriptSentBadge.js";
import { OpportunityHubSetBadge } from "./OpportunityHubSetBadge.js";

/** Collapsible opportunity header on Scripts & Briefs, styled like the single-set Scripts tab. */
export function OpportunityHubScriptGroupHeader({
  group,
  expanded,
  showSetName,
  striped = false,
  set,
  onToggle,
  onPeek,
}: {
  group: HubChromeScriptGroup;
  expanded: boolean;
  /** Several sets are listed, so the header names the set it belongs to. */
  showSetName: boolean;
  /** Alternate group backgrounds so adjacent headers read as separate rows. */
  striped?: boolean;
  /** The group's set, when loaded, so the badge can list its parameters on hover. */
  set?: PersonaOpportunitySet | null;
  onToggle: () => void;
  /** Loads the group's rows so the sent badge can name what was sent. */
  onPeek?: () => void;
}) {
  const { t } = useTranslation();
  const title = group.isCustom
    ? t("personaOpportunities.scripts.customGroupTitle", "Custom Scripts")
    : group.label;

  return (
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      data-striped={striped || undefined}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors duration-150",
        "hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        striped ? "bg-muted/40" : "bg-background",
      )}
    >
      <Icon
        name="chevron-right"
        size="sm"
        className={cn(
          "shrink-0 text-muted-foreground transition-transform duration-150 ease-out motion-reduce:transition-none",
          expanded && "rotate-90",
        )}
        aria-hidden
      />
      {group.isCustom ? (
        <span
          className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
          data-testid="opportunity-hub-script-group-custom-mark"
        >
          <Icon name="sparkles" size="sm" color="inherit" aria-hidden />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold leading-snug text-foreground">
          {title}
        </span>
        {group.personaTitle ? (
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {group.personaTitle}
          </span>
        ) : null}
      </span>
      {showSetName ? <OpportunityHubSetBadge name={group.setName} set={set} /> : null}
      <OpportunityHubScriptSentBadge
        items={group.sentItems}
        isLoadingTitles={group.isLoadingSentTitles}
        onPeek={onPeek}
      />
      <OpportunityHubScriptCountBadge
        title={title}
        personaTitle={group.personaTitle}
        scriptCount={group.scriptCount}
        briefCount={group.briefCount}
      />
    </button>
  );
}
